import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Surface } from '@heroui/react';
import { toast } from '@/lib/toast';
import { errToToast } from '@/lib/humanizeApiError';
import { Button } from '@/components/Button';
import { Switch } from '@/components/ui/Switch';
import { updateUiPrefs, useSystemSoundsMuted } from '@/lib/uiPrefs';
import { isNativeApp } from '@/lib/isNativeApp';
import { isDesktopShell } from '@/lib/desktopBridge';
import {
  areNotificationsEnabled,
  disableNotifications,
  notificationsSupported,
  requestNotifications,
} from '@/lib/notifications';
import {
  disableBrowserPush,
  enableBrowserPush,
  getActiveSubscription,
  getPushPermission,
  getServerPushStatus,
  resyncBrowserPush,
  supportsPush,
} from '@/lib/webPush';

/**
 * UserSettings → Уведомления tab. Two layers:
 *
 *  1. Device notifications (OS banners) — the one that actually matters and
 *     works everywhere the app is *running*, including the Electron desktop
 *     shell (which sits in the tray with a live socket) and a backgrounded
 *     browser tab. The delivery path (lib/notifications.ts → new Notification,
 *     fired on dm:activity / channel:activity for messages, mentions, replies)
 *     was wired long ago but had no opt-in UI, so it never fired. This is it.
 *
 *  2. Background web-push (browser only) — VAPID + Service Worker, the only
 *     thing that reaches you when the *tab/browser is fully closed*. Electron
 *     has no push service, so this card is hidden in the desktop app.
 */
export function NotificationsTab() {
  // Web-push (VAPID + Service Worker) only reaches a *fully closed* browser.
  // It can't work in the Capacitor mobile shell OR the Electron desktop shell
  // (no push service) — both error on "enable". Hide it there; device
  // notifications (above) cover them while the app runs / sits in the tray.
  const noWebPush = isNativeApp() || isDesktopShell();
  return (
    <div className="app-settings-groups flex flex-col gap-5">
      {/* Desktop draws its own card for messages (lib/dmToast.ts), so no OS banner switch there. */}
      {!isDesktopShell() && <DeviceNotifications />}
      <SystemSounds />
      {!noWebPush && <BackgroundPush />}
    </div>
  );
}

/**
 * One switch for every cue the app makes about itself — join/leave, the message
 * ping, the screen-share chime. It deliberately does NOT touch anything you
 * came here to hear: voices in a call, the sound of a shared screen, voice
 * messages. Those play through the voice pipeline, not through lib/sounds, so
 * the split is structural rather than a list that can drift.
 */
function SystemSounds() {
  const { t } = useTranslation();
  const muted = useSystemSoundsMuted();

  return (
    <Surface variant="secondary" className="rounded-xl p-5">
      <div className="flex min-w-0 items-center justify-between gap-4">
        <div className="app-settings-group-title text-[15px] font-semibold tracking-[-0.01em] text-ink">
          {t('prof.notif.soundsTitle')}
        </div>
        <Switch
          checked={!muted}
          onChange={(on) => updateUiPrefs({ systemSoundsMuted: !on })}
          label={t('prof.notif.soundsTitle')}
        />
      </div>
      <p className="mt-2 text-xs leading-relaxed text-ink-muted">{t('prof.notif.soundsDesc')}</p>
    </Surface>
  );
}

/** OS banner notifications — works in the desktop shell + backgrounded tabs. */
function DeviceNotifications() {
  const { t } = useTranslation();
  const [enabled, setEnabled] = useState(() => areNotificationsEnabled());
  const [supported, setSupported] = useState(() => notificationsSupported());
  const [busy, setBusy] = useState(false);

  // Notification.permission can change with no React event (user revokes it in
  // browser/OS settings while this tab is open). Re-sync when the tab regains
  // focus and on the permission-status 'change' event so the toggle never shows
  // a stale "enabled" state.
  useEffect(() => {
    const sync = () => {
      setEnabled(areNotificationsEnabled());
      setSupported(notificationsSupported());
    };
    document.addEventListener('visibilitychange', sync);
    let status: PermissionStatus | null = null;
    navigator.permissions
      ?.query({ name: 'notifications' as PermissionName })
      .then((p) => {
        status = p;
        p.addEventListener('change', sync);
      })
      .catch(() => {});
    return () => {
      document.removeEventListener('visibilitychange', sync);
      status?.removeEventListener('change', sync);
    };
  }, []);

  async function enable() {
    setBusy(true);
    try {
      const ok = await requestNotifications();
      setEnabled(ok);
      if (ok) toast.success(t('prof.notif.osEnabledToast'));
      else toast.error(t('prof.notif.osBlocked'));
    } finally {
      setBusy(false);
    }
  }

  function disable() {
    disableNotifications();
    setEnabled(false);
    toast.success(t('prof.notif.osDisabledToast'));
  }

  return (
    <Surface variant="secondary" className="rounded-xl p-5">
      <div className="min-w-0">
        <div className="app-settings-group-title text-[15px] font-semibold tracking-[-0.01em] text-ink">
          {t('prof.notif.osTitle')}
        </div>
        <p className="mt-1 text-xs leading-relaxed text-ink-muted">{t('prof.notif.osDesc')}</p>
        <NotificationState state={!supported ? 'blocked' : enabled ? 'on' : 'off'}>
          {!supported ? null : enabled ? (
            <Button variant="ghost" onClick={disable}>
              {t('prof.notif.disableBtn')}
            </Button>
          ) : (
            <Button variant="primary" onClick={() => void enable()} loading={busy}>
              {t('prof.notif.enableBtn')}
            </Button>
          )}
        </NotificationState>
      </div>
    </Surface>
  );
}

/** Web-push: reaches you even when the browser tab is fully closed. Browser
 *  only — the caller hides this in the Electron shell. */
function BackgroundPush() {
  const { t } = useTranslation();
  const supported = supportsPush();
  const [permission, setPermission] = useState<NotificationPermission>(() =>
    supported ? getPushPermission() : 'denied',
  );
  const [serverEnabled, setServerEnabled] = useState<boolean | null>(null);
  const [hasSubscription, setHasSubscription] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!supported) {
      setServerEnabled(false);
      setHasSubscription(false);
      return;
    }
    let cancelled = false;
    // The switch shows what the server holds: an existing browser
    // subscription is registered again (it is bound to the current sign-in),
    // and shows as on only once the server has it.
    Promise.all([getServerPushStatus(), getActiveSubscription()])
      .then(async ([status, sub]) => {
        if (cancelled) return;
        setServerEnabled(status.enabled);
        const registered = status.enabled && !!sub && (await resyncBrowserPush().catch(() => false));
        if (!cancelled) setHasSubscription(registered);
      })
      .catch(() => {
        if (cancelled) return;
        setServerEnabled(false);
        setHasSubscription(false);
      });
    return () => {
      cancelled = true;
    };
  }, [supported]);

  async function onEnable() {
    setBusy(true);
    try {
      await enableBrowserPush();
      setPermission(getPushPermission());
      setHasSubscription(true);
      toast.success(t('prof.notif.enabledToast'));
    } catch (err) {
      toast.error(...errToToast(err, t('prof.notif.enableFailed')));
    } finally {
      setBusy(false);
    }
  }

  async function onDisable() {
    setBusy(true);
    try {
      await disableBrowserPush();
      setHasSubscription(false);
      toast.success(t('prof.notif.disabledToast'));
    } catch (err) {
      toast.error(...errToToast(err, t('prof.notif.disableFailed')));
    } finally {
      setBusy(false);
    }
  }

  // VAPID is optional. Until the server can deliver push, leave this control
  // out of settings instead of showing a full card with no usable action.
  if (!supported || serverEnabled !== true) return null;

  return (
    <Surface variant="secondary" className="rounded-xl p-5">
      <div className="min-w-0">
        <div className="app-settings-group-title text-[15px] font-semibold tracking-[-0.01em] text-ink">
          {t('prof.notif.pushTitle')}
        </div>
        <p className="mt-1 text-xs leading-relaxed text-ink-muted">{t('prof.notif.pushDesc')}</p>
        <NotificationState
          state={
            permission === 'denied' ? 'blocked' : hasSubscription === null ? null : hasSubscription ? 'on' : 'off'
          }
        >
          <ToggleAction
            permission={permission}
            hasSubscription={hasSubscription}
            busy={busy}
            onEnable={() => void onEnable()}
            onDisable={() => void onDisable()}
          />
        </NotificationState>
      </div>
    </Surface>
  );
}

type NotificationStateKind = 'on' | 'off' | 'blocked';

const STATE_KEY: Record<NotificationStateKind, string> = {
  on: 'prof.notif.stateOn',
  off: 'prof.notif.stateOff',
  blocked: 'prof.notif.stateBlocked',
};

/** One status line per card — what the person gets now — next to the action. */
function NotificationState(props: { state: NotificationStateKind | null; children?: ReactNode }) {
  const { t } = useTranslation();
  return (
    <div className="mt-4 flex min-w-0 flex-wrap items-center justify-between gap-3">
      <div
        role="status"
        className={props.state === 'blocked' ? 'text-xs text-danger-ink' : 'text-xs text-ink-muted'}
      >
        {props.state && t(STATE_KEY[props.state])}
      </div>
      {props.children}
    </div>
  );
}

function ToggleAction(props: {
  permission: NotificationPermission;
  hasSubscription: boolean | null;
  busy: boolean;
  onEnable: () => void;
  onDisable: () => void;
}) {
  const { t } = useTranslation();
  if (props.permission === 'denied') return null;
  if (props.hasSubscription) {
    return (
      <Button variant="ghost" onClick={props.onDisable} loading={props.busy}>
        {t('prof.notif.disableBtn')}
      </Button>
    );
  }
  return (
    <Button variant="primary" onClick={props.onEnable} loading={props.busy}>
      {t('prof.notif.enableBtn')}
    </Button>
  );
}
